<template>
    <div class="sequenced-note-reading-question">
        <!-- Instructions -->
         <div class="instruction-bar">
            <div class="instruction-text">
                Play the notes:
                <span v-if="question?.label" class="label">{{ question.label }}</span>
            </div>
         </div>  
        
        <!-- <h3>Play the notes:</h3>
        <h2>{{ question.label }}</h2> -->

        <!-- SequencedNotePlayer component -->
        <SequencedNotePlayer
            v-if="question"
            :sequence="sequence"
            :tolerance-cents="question.toleranceCents || 40"
            :strict-cents="true"
            :allow-octave-errors="false"
            :dropout-buffer-frames="5"
            :show-fingerings="question.showFingerings !== false"
            :show-pitch-visualizer="question.showPitchVisualizer === true"
            :pitch-visualizer-variant="question.pitchVisualizerVariant || 'minimal'"
            :key-sig="question.keySig"
            :auto-start="true"
            :hide-controls="true"
            @sequence-complete="handleSequenceComplete"
            @sequence-failed="handleSequenceFailed"
            @note-matched="handleNoteMatched"
            @wrong-note="handleWrongNote"
            @note-ended="handleNoteEnded"
        />
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    computed,
    onMounted,
    PropType,
    defineOptions,
} from "vue";
import { useViewable, useQuestionView } from "@vue-skuilder/courseware";
import { ViewData } from "@vue-skuilder/common";
import SequencedNotePlayer from "../components/SequencedNotePlayer.vue";
import {
    SequencedNoteReadingQuestion,
    type SequencedNoteReadingAnswer,
} from "./SequencedNoteReadingQuestion";

defineOptions({
    name: "SequencedNoteReadingQuestionView",
});

const props = defineProps({
    data: {
        type: Array as PropType<ViewData[]>,
        required: true,
    },
    modifyDifficulty: {
        type: Number,
        required: false,
        default: 0,
    },
});

const emit = defineEmits(["emitResponse", "note-matched", "wrong-note", "sequence-complete", "sequence-failed", "note-ended"]);

// Use Skuilder composables
const viewableUtils = useViewable(props, emit, "SequencedNoteReadingQuestionView");
const questionUtils = useQuestionView<SequencedNoteReadingQuestion>(viewableUtils);

// State
const startTime = ref<number>(0);
const wrongAttempts = ref<number>(0);

// Computed - question.value should now have the parsed sequence array
const question = computed(() => questionUtils.question.value);
const sequence = computed(() => question.value?.sequence || []);

// Event handlers
function handleNoteMatched(note: string, index: number) {
    console.log(`Note matched: ${note} at index ${index}`);
    emit('note-matched', note, index);
}

function handleWrongNote(detected: string, expected: string) {
    wrongAttempts.value++;
    console.log(`Wrong note: played ${detected}, expected ${expected}`);
    emit('wrong-note', detected, expected);
}

function handleNoteEnded(note: string) {
    console.log(`Note ended: ${note}`);
    emit('note-ended', note);
}

function handleSequenceComplete(totalAttempts: number) {
    const timeElapsed = Date.now() - startTime.value;

    const answer: SequencedNoteReadingAnswer = {
        sequence: sequence.value,
        completed: true,
        wrongAttempts: wrongAttempts.value,
        timeElapsed,
    };

    emit('sequence-complete', totalAttempts);
    questionUtils.submitAnswer(answer);
}

function handleSequenceFailed(totalAttempts: number) {
    const timeElapsed = Date.now() - startTime.value;

    const answer: SequencedNoteReadingAnswer = {
        sequence: sequence.value,
        completed: false,
        wrongAttempts: totalAttempts,
        timeElapsed,
    };

    console.log(`callingsubmitAnswer`);
    emit('sequence-failed', totalAttempts);
    questionUtils.submitAnswer(answer);
}

// Lifecycle
onMounted(() => {
    // Initialize question
    questionUtils.question.value = new SequencedNoteReadingQuestion(props.data);
    questionUtils.maxAttemptsPerView.value = 1;
    startTime.value = Date.now();
});
</script>

<style scoped>
.sequenced-note-reading-question {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
}

.instruction-bar {
    display: flex;
    justify-content: center;
    padding: 16px;
    background: rgb(var(--v-theme-surface-light));
    border-radius: 8px;
}

.instruction-text {
    font-size: 1.1rem;
    color: rgb(var(--v-theme-on-surface));
    display: flex;
    align-items: left;
    gap: 8px;
}

.label {
    display: block;
    padding: 4px 12px;
    background:rgb(var(--v-theme-secondary));
    border-radius: 4px;
    font-weight: 600;
}
</style>
